import { useEffect, useRef, useState } from "react";

const IDLE_LIMIT_MS = 15 * 60 * 1000; // 15 min total idle → logout
const WARN_BEFORE_MS = 60 * 1000; // show warning 60s before logout

export default function useIdleTimeout({ onTimeout }) {
  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(WARN_BEFORE_MS / 1000);

  // Stable refs so the effect doesn't tear down/restart on every render
  const onTimeoutRef = useRef(onTimeout);
  const showWarningRef = useRef(false);
  const lastActivity = useRef(Date.now());
  const loggedOut = useRef(false);

  // Keep refs in sync with latest values
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  const reset = () => {
    lastActivity.current = Date.now();
    showWarningRef.current = false;
    setShowWarning(false);
    setCountdown(WARN_BEFORE_MS / 1000);
  };

  useEffect(() => {
    // Clicks/taps only — mouse movement, keyboard, scroll do NOT count as activity
    const events = ["mousedown", "touchstart"];

    const handleActivity = () => {
      // Don't reset just by clicking the warning modal's overlay or buttons
      // (the buttons themselves call reset() explicitly via onStay)
      // — but do reset for any click while the warning is NOT shown.
      if (!showWarningRef.current) {
        lastActivity.current = Date.now();
      }
    };

    events.forEach((e) =>
      window.addEventListener(e, handleActivity, { passive: true }),
    );

    const tick = setInterval(() => {
      if (loggedOut.current) return;

      const idle = Date.now() - lastActivity.current;

      if (idle >= IDLE_LIMIT_MS) {
        loggedOut.current = true;
        clearInterval(tick);
        onTimeoutRef.current?.();
        return;
      }

      if (idle >= IDLE_LIMIT_MS - WARN_BEFORE_MS) {
        if (!showWarningRef.current) {
          showWarningRef.current = true;
          setShowWarning(true);
        }
        const remaining = Math.ceil((IDLE_LIMIT_MS - idle) / 1000);
        setCountdown(remaining > 0 ? remaining : 0);
      }
    }, 1000);

    return () => {
      events.forEach((e) => window.removeEventListener(e, handleActivity));
      clearInterval(tick);
    };
  }, []);

  return { showWarning, countdown, reset };
}
